<template>
  <div :class="wrapperClassName" v-if="visibleIfResult" id="wdw-library-form-control-datetime">
    <template v-if="!hidden">
      <label :class="labelClassName" :for="idInput" v-dompurify-html="labelText" :aria-label="label"></label>
      <span
        class="help-trigger"
        :title="domainStore.translate('general.form.show-help-tooltip')"
        @click="toggleHelpText"
        v-if="help"
      >
        <font-awesome-icon icon="question-circle" size="lg" />
      </span>
      <Message
        v-show="shouldShowHelp"
        closable
        severity="info"
        role="alert"
        class="mb-2 form-info-message"
        @close="toggleHelpText"
      >
        <span v-dompurify-html="help"></span>
      </Message>
    </template>

    <div>
      <Calendar
        :id="idInput"
        :data-wdw-code="code"
        :data-vv-as="labelText"
        :name="name"
        :required="required"
        :placeholder="placeholder"
        panelClass="wdw-vue-widgets"
        :inputClass="inputClassName"
        v-model="value"
        tabindex="0"
        :aria-invalid="hasError || hasServerError"
        :aria-describedby="idErrorText"
        :hidden="hidden"
        :disabled="readonly"
        :showTime="!dateOnly"
        hourFormat="24"
        :minDate="minDate"
        :maxDate="maxDate"
        :showIcon="true"
        icon="pi pi-calendar"
        dateFormat="dd.mm.yy"
        :hideOnDateTimeSelect="true"
        :showButtonBar="true"
      />
    </div>
    <template v-if="!hidden">
      <span :id="idErrorText" v-show="hasError" class="text-danger form-error-message" aria-atomic="true">
        {{ error }}
      </span>
      <span :id="idErrorText" v-show="hasServerError" class="text-danger form-error-message" aria-atomic="true">
        {{ serverError }}
      </span>
    </template>
  </div>
</template>

<script>
import { computed, watch, toRefs } from 'vue';
import { useField } from 'vee-validate';
import useInputValidate from '@/composables/general/useInputValidate';
import useToggler from '@/composables/general/useToggler';
import { domainStore } from '@/store/domain-store';
import moment from 'moment';
import Calendar from 'primevue/calendar';
import Message from 'primevue/message';

export default {
  name: 'form-control-datetime',

  props: {
    name: {
      required: true,
      type: String,
    },
    code: {
      required: false,
      type: String,
      default: '',
    },
    label: {
      required: false,
      type: String,
      default: '',
    },
    required: {
      required: false,
      type: Boolean,
      default: false,
    },
    placeholder: {
      required: false,
      type: String,
      default: '',
    },
    defaultvalue: {
      required: false,
      type: String,
      default: '',
    },
    shouldcache: {
      required: false,
      type: Boolean,
      default: false,
    },
    additionalLabelClasses: {
      required: false,
      type: String,
      default: '',
    },
    error: {
      required: false,
      type: String,
      default: '',
    },
    serverError: {
      required: false,
      type: String,
      default: '',
    },
    help: {
      required: false,
      type: String,
      default: '',
    },
    hidden: {
      required: false,
      type: Boolean,
      default: false,
    },
    prefixId: {
      required: false,
      type: String,
      default: 'wdw-form-attr',
    },
    visibleIfResult: {
      required: false,
      type: Boolean,
      default: true,
    },
    readonly: {
      required: false,
      type: Boolean,
    },
    min: {
      required: false,
      type: String,
    },
    max: {
      required: false,
      type: String,
    },
    dateOnly: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  emits: ['validate'],

  setup(props, context) {
    const {
      name,
      code,
      label,
      min,
      max,
      required,
      defaultvalue,
      error,
      serverError,
      additionalLabelClasses,
      shouldcache,
      prefixId,
    } = toRefs(props);

    const { isValidEmail, isValidURL } = useInputValidate();
    const { toggleValue: shouldShowHelp, toggle: toggleHelpText } = useToggler();
    const minDate = min.value ? new Date(min.value) : null;
    const maxDate = max.value ? new Date(max.value) : null;

    //+---------------------+
    //|    Other functions  |
    //+---------------------+
    function validate(value) {
      // vee-validate's composition API
      const finalvalue = value ? value : '';
      const returnValue = {
        value: finalvalue,
        valueHuman: finalvalue,
        validated: true,
        name: name.value,
        code: code.value,
      };
      if (required.value) {
        const finaldate = moment(finalvalue);
        let validated = true;
        if (!finaldate.isValid()) {
          validated = false;
        }
        if (min.value) {
          const mindate = moment(min.value);
          // check that selected date is after provided min date
          if (mindate.isValid && !finaldate.isSameOrAfter(mindate, 'day')) {
            validated = false;
          }
        }
        if (max.value) {
          const maxdate = moment(max.value);
          // check that selected date is before provided max date
          if (maxdate.isValid && !finaldate.isSameOrBefore(maxdate, 'day')) {
            validated = false;
          }
        }
        returnValue.validated = validated;
      }

      if (shouldcache.value) {
        localStorage.setItem(name.value, returnValue.value);
      }

      // Emit event
      context.emit('validate', returnValue);
      return returnValue.validated ? true : error.value;
    }
    const { errorMessage, value } = useField(name, validate, {
      initialValue: defaultvalue.value ? new Date(defaultvalue.value) : null,
    });

    watch(defaultvalue, (nV, oV) => {
      value.value = nV ? new Date(nV) : null;
    });

    //+---------------------+
    //|    computed()       |
    //+---------------------+
    const hasError = computed(() => {
      return errorMessage.value != undefined;
    });
    const hasServerError = computed(() => {
      return serverError.value != undefined && serverError.value !== '';
    });
    const wrapperClassName = computed(() => {
      return {
        'form-group': true,
        'has-error': hasError.value || hasServerError.value,
      };
    });
    const labelClassName = computed(() => {
      return additionalLabelClasses.value + ' ' + (hasError.value || hasServerError.value ? 'text-danger' : '');
    });
    const inputClassName = computed(() => {
      return {
        'form-control': true,
        'is-invalid': hasError.value || hasServerError.value,
      };
    });
    const labelText = computed(() => {
      return label.value + (required.value ? '*' : '');
    });
    const idInput = computed(() => {
      return prefixId.value + '-text-' + name.value;
    });
    const idErrorText = computed(() => {
      return prefixId.value + '-text-' + name.value + '-error';
    });

    //+---------------------+
    //|  Lifecycle Hooks    |
    //+---------------------+

    //+---------------------+
    //|    return data      |
    //+---------------------+
    return {
      domainStore,
      // Computed
      hasError,
      hasServerError,
      wrapperClassName,
      labelClassName,
      inputClassName,
      idInput,
      idErrorText,
      labelText,
      // Methods
      toggleHelpText,
      // Variables
      shouldShowHelp,
      errorMessage,
      minDate,
      maxDate,
      value,
    };
  },
  components: {
    Calendar,
    Message,
  },
};
</script>

<style lang="scss" scoped>
.p-calendar.p-component.p-inputwrapper {
  display: flex;
}
label {
  display: inline;
  font-weight: bold;
  max-width: 100%;
}
input {
  margin-top: 5px;
}
.form-group {
  margin-bottom: 15px;
}
.help-trigger {
  cursor: pointer;
  margin-left: 4px;
}
</style>
